/**
 * A post or comment body, as plain text — whatever the wire carried.
 *
 * The contract is plain text with blank-line paragraphs, and every render site
 * prints it verbatim. The community bodies migrated from WordPress arrived as
 * HTML for a while (`<p>Voici notre premier défi du mois de <b>juin </b>☀</p>`)
 * and both apps printed the tags. The data was converted server-side with this
 * same function (`laboussle-backend/domain/src/community/html-to-text.ts`),
 * and this copy is the belt-and-braces: a stray tag, entity or `addslashes`
 * artefact that reaches a client still cannot print.
 *
 * It is a near no-op on plain text — a body with no tag-shaped sequence only
 * gets its entities decoded and its whitespace settled — and idempotent, so
 * running it over a body already converted changes nothing. `<3`, `a < b` and
 * `5 > 3` are not tags and survive intact.
 *
 * **One copy per client `packages/domain`, byte-identical to the other**, per
 * the copy-packages rule; `tests/plain-body.parity.test.ts` refuses drift.
 */
/** Tags whose boundary is a paragraph break in the plain-text convention. */
const BLOCK = 'p|div|h[1-6]|blockquote|ul|ol|table|thead|tbody|tr|pre|section|article|figure|figcaption|hr|header|footer|address';
const TAG = /<[a-z][^>]*>/i;
const NAMED_ENTITIES = {
    amp: '&', lt: '<', gt: '>', quot: '"', apos: "'", nbsp: ' ',
    hellip: '…', ndash: '–', mdash: '—', lsquo: '‘', rsquo: '’', ldquo: '“', rdquo: '”',
    laquo: '«', raquo: '»', bull: '•', middot: '·', copy: '©', reg: '®', trade: '™',
    euro: '€', deg: '°', times: '×', divide: '÷', plusmn: '±', para: '¶', sect: '§',
    iexcl: '¡', iquest: '¿', cent: '¢', pound: '£', yen: '¥',
    agrave: 'à', aacute: 'á', acirc: 'â', atilde: 'ã', auml: 'ä', aring: 'å', aelig: 'æ',
    ccedil: 'ç', egrave: 'è', eacute: 'é', ecirc: 'ê', euml: 'ë',
    igrave: 'ì', iacute: 'í', icirc: 'î', iuml: 'ï', ntilde: 'ñ',
    ograve: 'ò', oacute: 'ó', ocirc: 'ô', otilde: 'õ', ouml: 'ö', oslash: 'ø', oelig: 'œ',
    ugrave: 'ù', uacute: 'ú', ucirc: 'û', uuml: 'ü', yacute: 'ý', yuml: 'ÿ', szlig: 'ß',
    Agrave: 'À', Aacute: 'Á', Acirc: 'Â', Atilde: 'Ã', Auml: 'Ä', Aring: 'Å', AElig: 'Æ',
    Ccedil: 'Ç', Egrave: 'È', Eacute: 'É', Ecirc: 'Ê', Euml: 'Ë',
    Igrave: 'Ì', Iacute: 'Í', Icirc: 'Î', Iuml: 'Ï', Ntilde: 'Ñ',
    Ograve: 'Ò', Oacute: 'Ó', Ocirc: 'Ô', Otilde: 'Õ', Ouml: 'Ö', Oslash: 'Ø', OElig: 'Œ',
    Ugrave: 'Ù', Uacute: 'Ú', Ucirc: 'Û', Uuml: 'Ü', Yacute: 'Ý',
};
/** `&amp;`, `&eacute;`, `&#233;`, `&#xE9;` → the character. Unknown names are left as written. */
export function decodeEntities(text) {
    if (!text.includes('&'))
        return text;
    return text.replace(/&(#x[0-9a-f]+|#[0-9]+|[a-z][a-z0-9]*);/gi, (whole, ref) => {
        if (ref[0] === '#') {
            const code = ref[1] === 'x' || ref[1] === 'X' ? Number.parseInt(ref.slice(2), 16) : Number.parseInt(ref.slice(1), 10);
            if (!Number.isFinite(code) || code > 0x10ffff)
                return whole;
            // A control character or a surrogate half is not text anybody meant.
            if (code < 0x20 && code !== 0x0a && code !== 0x09)
                return '';
            if (code >= 0xd800 && code <= 0xdfff)
                return '';
            return String.fromCodePoint(code);
        }
        const named = NAMED_ENTITIES[ref];
        return named === undefined ? whole : named;
    });
}
function attribute(tag, name) {
    const match = new RegExp(`\\s${name}\\s*=\\s*(?:"([^"]*)"|'([^']*)'|([^\\s>]+))`, 'i').exec(tag);
    if (!match)
        return null;
    return (match[1] ?? match[2] ?? match[3] ?? '').trim();
}
/** A link worth spelling out is one a reader could follow; a placeholder or an anchor is not. */
function followableHref(href) {
    if (!href)
        return null;
    const decoded = decodeEntities(href);
    return /^(https?:\/\/|mailto:)/i.test(decoded) ? decoded : null;
}
function sameDestination(text, href) {
    const norm = (s) => s.trim().replace(/^mailto:/i, '').replace(/^https?:\/\//i, '').replace(/\/$/, '').toLowerCase();
    return norm(text) === norm(href);
}
/**
 * Settle whitespace to the convention: no trailing space on a line, at most one
 * blank line between paragraphs, nothing at either end. Newlines are the only
 * whitespace with meaning, so they are the only kind this touches by default;
 * `inline` also collapses runs of spaces, which is right for text that came out
 * of HTML (where a run of spaces was always one) and wrong for text a member
 * typed.
 */
function settle(text, inline) {
    let out = text.replace(/\r\n?/g, '\n');
    if (inline)
        out = out.replace(/[ \t\u00a0]+/g, ' ');
    return out
        .split('\n')
        .map((line) => (inline ? line.trim() : line.replace(/[ \t\u00a0]+$/g, '')))
        .join('\n')
        .replace(/\n{3,}/g, '\n\n')
        .trim();
}
/**
 * WordPress stored what PHP's `addslashes` handed it, so a third of the rows
 * carry `J\'avais` and `src=\"…\"`. Undone before anything reads an attribute,
 * or the escaped quote inside `src=\"…\"` hides the URL an embed would yield.
 */
export function stripSlashes(text) {
    return text.includes('\\') ? text.replace(/\\(['"\\])/g, '$1') : text;
}
function stripOnce(html) {
    let out = stripSlashes(html)
        // Not content, and their bodies are not either.
        .replace(/<!--[\s\S]*?-->/g, '')
        .replace(/<(script|style|template|noscript)\b[^>]*>[\s\S]*?<\/\1\s*>/gi, '')
        // Source whitespace beside a block boundary is formatting, not text: without
        // this, `</p>\r\n<p>` reads as three breaks and `<ul>\n<li>` as a blank item.
        .replace(new RegExp(`\\s+(?=<\\/?(?:${BLOCK}|li|br)\\b)`, 'gi'), '')
        .replace(new RegExp(`(<\\/?(?:${BLOCK}|li|br)\\b[^>]*>)\\s+`, 'gi'), '$1');
    // Inline elements that carry something a reader would lose with the tag.
    out = out.replace(/<a\b([^>]*)>([\s\S]*?)<\/a\s*>/gi, (_whole, attrs, inner) => {
        const href = followableHref(attribute(`<a${attrs}>`, 'href'));
        const text = inner.trim();
        if (!href)
            return inner;
        if (!text)
            return href;
        return sameDestination(stripOnce(text), href) ? inner : `${inner} (${href})`;
    });
    out = out.replace(/<img\b([^>]*)\/?>/gi, (_whole, attrs) => {
        const alt = attribute(`<img${attrs}>`, 'alt');
        if (alt)
            return alt;
        const src = followableHref(attribute(`<img${attrs}>`, 'src'));
        return src ? `\n${src}\n` : '';
    });
    // An embed is a link to the thing embedded; dropping it leaves a post that
    // was only a video with nothing in it.
    out = out.replace(/<(?:iframe|video|audio|source|embed)\b([^>]*)\/?>/gi, (_whole, attrs) => {
        const src = followableHref(attribute(`<x${attrs}>`, 'src'));
        return src ? `\n${src}\n` : '';
    });
    out = out
        .replace(/<br\b[^>]*\/?>/gi, '\n')
        // `<li><p>text</p></li>` is how the block editor writes a plain item; the
        // paragraph inside is the item, not a paragraph break around it.
        .replace(/(<li\b[^>]*>)\s*<p\b[^>]*>/gi, '$1')
        .replace(/<\/p\s*>\s*(?=<\/li\s*>)/gi, '')
        .replace(/<li\b[^>]*>/gi, '\n• ')
        .replace(/<\/li\s*>/gi, '')
        .replace(new RegExp(`<\\/?(?:${BLOCK})\\b[^>]*\\/?>`, 'gi'), '\n\n')
        // Everything left — <b>, <span style>, <em>, <u>, the lot — is unwrapped.
        .replace(/<\/?[a-z][^>]*>/gi, '')
        // A bullet left alone on its line (a `<li><div>…`) takes the text under it.
        .replace(/•[ \t]*\n+[ \t]*/g, '• ');
    return decodeEntities(out);
}
/**
 * Convert one body. Safe on anything: HTML, plain text, empty.
 *
 * Runs the strip until the result carries no tag, up to a small bound, because
 * some rows were escaped a second time (`&lt;div&gt;`) and only reveal their
 * markup once the entities are decoded. The bound is there so a body that
 * contains the *text* `<b>` cannot spin.
 */
export function htmlToText(input) {
    if (!input)
        return '';
    if (!TAG.test(input)) {
        // Plain text — unless decoding its entities reveals markup that had been
        // escaped once (`&lt;div&gt;` with nothing else), which is then HTML after all.
        const decoded = decodeEntities(stripSlashes(input));
        if (!TAG.test(decoded))
            return settle(decoded, false);
    }
    let out = input;
    for (let pass = 0; pass < 3 && TAG.test(decodeEntities(out)); pass += 1) {
        out = stripOnce(out);
    }
    return settle(out, true);
}
/** True when a stored body still carries markup — the backfill's "affected" predicate, kept beside the fix. */
export function carriesMarkup(body) {
    return TAG.test(body);
}
/** The body to print. Named for what a render site asks, not for what it does. */
export const plainBody = htmlToText;
//# sourceMappingURL=plain-body.js.map